import { reactive } from 'vue';

/**
 * Bridges Lumi's entry point out of LumiShell.vue's own render tree into
 * Sidebar.vue's footer row (make brief ai-switch-notify-launcher, outcome
 * invariants 9-10). The two are unrelated siblings in every module's
 * AppChrome — Sidebar sits in AppShell's `#sidebar` slot, LumiShell lives
 * inside LumiAssistantPanel in the `#dock` slot — so a prop/event pair cannot
 * cross between them without touching all 7 AppChrome hosts. Both files live
 * in this same component-lib package (the ADR 0014 carve-out for shared UI),
 * so a tiny module-scoped reactive singleton is the lightest bridge that
 * needs zero host-file changes.
 *
 * The sidebar button is the way in — with AppShell's app-bar button beside it
 * on a phone, where the sidebar is a closed drawer (both call `toggleLumiDock`
 * and read this same state): LumiShell's own affordances (a dock rail, then a
 * floating launcher pill) are both retired, so `visible` is
 * simply "a LumiShell is mounted" — every mode, every breakpoint, expanded or
 * not. Staying put while the panel is open is the point: the button toggles,
 * so the same click that opened Lumi closes it again. When no LumiShell has
 * mounted (AI unavailable, invariant 3) `visible` stays false and Sidebar
 * renders nothing to hold Lumi's place.
 *
 * `expanded` mirrors the mounted shell's state so the button can render as
 * pressed (`aria-expanded`, active styling) instead of silently toggling
 * something off-screen.
 *
 * `working` says Lumi is busy — a turn awaiting its answer or a run queued or
 * running — so the button's rays can pulse whether the panel is open or not
 * (LumiAssistantPanel owns that state and writes it here).
 *
 * `label` is the brand name "Lumi", not translated content (LumiDock.vue's
 * locale bundle deliberately omits `launcherLabel` for the same reason), so
 * this bridge introduces no new i18n surface.
 */
export interface LumiDockLauncherBridge {
  visible: boolean;
  expanded: boolean;
  working: boolean;
  label: string;
}

export const lumiDockLauncher = reactive<LumiDockLauncherBridge>({
  visible: false,
  expanded: false,
  working: false,
  label: 'lumi',
});

let toggleFn: (() => void) | null = null;
let openFn: (() => void) | null = null;
let focusFn: (() => void) | null = null;

/** Called by LumiShell on mount/unmount; `null` unregisters. */
export function setLumiDockLauncherToggle(fn: (() => void) | null): void {
  toggleFn = fn;
}

/** Called by Sidebar's new button on click. */
export function toggleLumiDock(): void {
  toggleFn?.();
}

/** Called by LumiShell on mount/unmount; `null` unregisters. */
export function setLumiDockLauncherOpen(fn: (() => void) | null): void {
  openFn = fn;
}

/** Opens Lumi in the dock (a stored mode wins) and never closes it — for a
 *  caller outside the shell that means "show me Lumi", like the demo bar's
 *  "Ask Lumi" task. No mounted shell (AI unavailable): a no-op. */
export function openLumiDock(): void {
  openFn?.();
}

/** Called by Sidebar's new button on mount/unmount; `null` unregisters. */
export function setLumiDockLauncherFocus(fn: (() => void) | null): void {
  focusFn = fn;
}

/** Called by LumiShell's minimize() so keyboard focus lands on the button
 *  that now represents the collapsed dock entry point, even though it lives
 *  in a different component instance's DOM subtree. */
export function focusLumiDock(): void {
  focusFn?.();
}
